import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  TrendingUp, 
  Users, 
  Search, 
  CheckCircle, 
  XCircle, 
  ChevronRight,
  ChevronLeft,
  Activity,
  Clock,
  Layers,
  Zap,
  MousePointer2,
  FileText,
  BarChart3,
  ShieldCheck,
  Smartphone,
  Globe,
  Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- Types ---

type Screen = 1 | 2 | 3 | 4 | 5 | 6 | 7;

interface Event {
  id: string;
  time: string;
  action: string;
  actor: string;
  location: string;
  device: string;
  risk: 'low' | 'medium' | 'high';
  explanation: string;
  icon: React.ReactNode;
}

// --- Data ---

const events: Event[] = [
  {
    id: 'e1',
    time: 'Day 1, 09:15 AM',
    action: 'New Device Registration',
    actor: 'Sarah Jenkins (CFO)',
    location: 'Chicago, IL',
    device: 'iPhone 15 Pro',
    risk: 'low',
    explanation: 'Sarah often travels for business. A new device upgrade is a common event for an executive.',
    icon: <Smartphone className="w-5 h-5" />
  },
  {
    id: 'e2',
    time: 'Day 2, 02:30 PM',
    action: 'Address Update',
    actor: 'Admin Portal',
    location: 'Remote IP',
    device: 'Chrome / Windows',
    risk: 'low',
    explanation: 'Secondary shipping address added for a regular vendor. Happens during quarterly reconciliations.',
    icon: <Globe className="w-5 h-5" />
  },
  {
    id: 'e3',
    time: 'Day 3, 11:00 AM',
    action: 'MFA Settings Modified',
    actor: 'Sarah Jenkins (CFO)',
    location: 'Chicago, IL',
    device: 'iPhone 15 Pro',
    risk: 'low',
    explanation: 'Backup email added to MFA profile. Often done to ensure access during international travel.',
    icon: <Lock className="w-5 h-5" />
  },
  {
    id: 'e4',
    time: 'Day 4, 10:20 AM',
    action: 'Vendor Wire Template Created',
    actor: 'Sarah Jenkins (CFO)',
    location: 'Chicago, IL',
    device: 'iPhone 15 Pro',
    risk: 'low',
    explanation: 'New template for "Global Logistics Ltd". Sarah manages high-value vendor relationships directly.',
    icon: <FileText className="w-5 h-5" />
  },
  {
    id: 'e5',
    time: 'Day 4, 11:45 AM',
    action: 'High-Value Wire Initiation',
    actor: 'Sarah Jenkins (CFO)',
    location: 'Chicago, IL',
    device: 'iPhone 15 Pro',
    risk: 'medium',
    explanation: '$145,000 to "Global Logistics Ltd". Slightly above average but within the account threshold.',
    icon: <TrendingUp className="w-5 h-5" />
  }
];

const rulesChecks = [
  { label: 'Amount within Daily Limit', status: 'pass', description: '$145k < $250k Limit' },
  { label: 'Known Device Fingerprint', status: 'pass', description: 'iPhone 15 Pro registered Day 1' },
  { label: 'MFA Verification', status: 'pass', description: 'Successfully authenticated via Backup Email' },
  { label: 'Geographic Consistency', status: 'pass', description: 'Chicago, IL matches User Profile' },
  { label: 'OFAC/Sanctions Screen', status: 'pass', description: 'No matches for "Global Logistics Ltd"' },
];

const aiInsights = [
  { title: 'Velocity Anomaly', detail: 'Sequence of Admin + Profile + Payment changes within 96 hours is atypical for this user.', weight: 0.85 },
  { title: 'Behavioral Biometrics', detail: 'Interaction patterns on mobile device (keystroke dynamics) deviate from historical baseline.', weight: 0.92 },
  { title: 'Pathways Analysis', detail: 'The "Global Logistics Ltd" entity shares metadata with accounts previously linked to BEC campaigns.', weight: 0.78 },
  { title: 'Temporal Context', detail: 'Modifications occurring shortly before high-value transfers mimic known Account Takeover journeys.', weight: 0.88 },
];

// --- Components ---

const Header = () => (
  <header className="flex items-center justify-between p-6 border-b border-white/10 bg-slate-900/50 backdrop-blur-md sticky top-0 z-50">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/20">
        <Shield className="text-white w-6 h-6" />
      </div>
      <div>
        <h1 className="text-xl font-bold tracking-tight text-white">Adaptive Fraud Fusion</h1>
        <p className="text-xs text-blue-400 font-medium tracking-widest uppercase">Precision Security Demo</p>
      </div>
    </div>
    <div className="flex items-center gap-6">
      <div className="hidden md:flex gap-4">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Activity className="w-4 h-4 text-green-400" />
          <span>System Active</span>
        </div>
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Clock className="w-4 h-4 text-blue-400" />
          <span>Real-time Monitoring</span>
        </div>
      </div>
    </div>
  </header>
);

const ProgressBar = ({ current }: { current: Screen }) => (
  <div className="fixed bottom-0 left-0 w-full h-1 bg-slate-800 z-50">
    <motion.div 
      className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
      initial={{ width: 0 }}
      animate={{ width: `${(current / 7) * 100}%` }}
      transition={{ type: 'spring', stiffness: 50, damping: 20 }}
    />
  </div>
);

const Navigation = ({ current, setScreen }: { current: Screen, setScreen: (s: Screen) => void }) => (
  <div className="fixed bottom-8 right-8 flex gap-3 z-50">
    {current > 1 && (
      <button 
        onClick={() => setScreen((current - 1) as Screen)}
        className="p-4 rounded-full bg-slate-800 text-white hover:bg-slate-700 transition-all border border-white/10 flex items-center gap-2 group"
      >
        <ChevronLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
        <span className="pr-2 font-medium">Back</span>
      </button>
    )}
    {current < 7 && (
      <button 
        onClick={() => setScreen((current + 1) as Screen)}
        className="p-4 pl-6 pr-4 rounded-full bg-blue-600 text-white hover:bg-blue-500 transition-all shadow-xl shadow-blue-600/30 flex items-center gap-3 group"
      >
        <span className="font-semibold text-lg">
          {current === 1 ? 'Start Demo' : 'Next Step'}
        </span>
        <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
      </button>
    )}
  </div>
);

// --- Screen Components ---

const Screen1 = () => (
  <div className="max-w-4xl mx-auto py-20 px-6 text-center">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold uppercase tracking-wider">
        The Modern Threat Landscape
      </div>
      <h2 className="text-5xl md:text-7xl font-extrabold text-white leading-tight">
        When the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Rules</span> Are No Longer Enough
      </h2>
      <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
        Sophisticated fraudsters don't break the rules anymore. They study them, learn the boundaries, and operate carefully inside them.
      </p>
      
      <div className="grid md:grid-cols-3 gap-8 pt-12">
        {[
          { icon: <Zap className="text-yellow-400" />, title: 'Adaptive Tactics', text: 'Fraudsters evolve faster than static rulebooks.' },
          { icon: <Layers className="text-blue-400" />, title: 'Invisible Patterns', text: 'They exploit the gaps between siloed monitoring systems.' },
          { icon: <MousePointer2 className="text-purple-400" />, title: 'Explainable Actions', text: 'Every single step they take looks legitimate in isolation.' }
        ].map((item, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.1 }}
            className="p-8 rounded-2xl bg-slate-800/40 border border-white/5 backdrop-blur-sm text-left hover:border-blue-500/30 transition-colors"
          >
            <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-6 border border-white/5">
              {item.icon}
            </div>
            <h3 className="text-white font-bold mb-3 text-lg">{item.title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
          </motion.div>
        ))}
      </div>
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="pt-16"
      >
        <p className="text-slate-500 font-medium">How do you catch a fraudster who follows every rule?</p>
      </motion.div>
    </motion.div>
  </div>
);

const Screen2 = () => (
  <div className="max-w-6xl mx-auto py-12 px-6">
    <div className="grid md:grid-cols-2 gap-16 items-center">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
        <h2 className="text-4xl font-bold text-white mb-6">The Legacy <br/><span className="text-slate-500">Rules-Based View</span></h2>
        <p className="text-lg text-slate-400 mb-8 leading-relaxed">
          Standard systems evaluate events as isolated data points. They check thresholds and explicit conditions. If it's not a direct violation, it's allowed.
        </p>
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex gap-4">
            <AlertTriangle className="text-yellow-500 w-6 h-6 shrink-0" />
            <p className="text-sm text-yellow-200/80">
              The case does not clearly violate any single "Hard Stop" rule. The system sees no reason to block the transaction.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-slate-800/50 border border-white/5">
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider opacity-50">Decision Outcome</h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="text-green-500" />
              </div>
              <div>
                <span className="text-2xl font-bold text-green-500">ALLOWED</span>
                <p className="text-slate-500 text-sm">Confidence: 94% (Rule Compliance)</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 rounded-3xl p-8 border border-white/10 shadow-2xl relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-6 opacity-5">
          <Shield className="w-48 h-48" />
        </div>
        <h3 className="text-white font-bold mb-8 flex items-center gap-3">
          <BarChart3 className="text-blue-500" />
          Legacy Compliance Engine
        </h3>
        <div className="space-y-6">
          {rulesChecks.map((rule, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-white/5 hover:bg-slate-800 transition-colors"
            >
              <div>
                <div className="text-white font-medium text-sm">{rule.label}</div>
                <div className="text-slate-500 text-xs mt-1">{rule.description}</div>
              </div>
              <div className="px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-bold border border-green-500/20">
                PASS
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </div>
);

const Screen3 = () => (
  <div className="max-w-5xl mx-auto py-12 px-6">
    <div className="text-center mb-16 space-y-4">
      <motion.h2 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl font-bold text-white"
      >
        Rules see events. <span className="text-blue-500">AI sees journeys.</span>
      </motion.h2>
      <p className="text-slate-400 text-lg max-w-2xl mx-auto">
        When we connect the dots across time, the story changes. Each step below is "individually explainable," but the sequence is not.
      </p>
    </div>

    <div className="relative space-y-8">
      <div className="absolute left-[39px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-blue-500/50 via-slate-700 to-transparent" />
      {events.map((event, i) => (
        <motion.div 
          key={event.id}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.2 }}
          className="relative pl-20"
        >
          <div className="absolute left-0 w-20 flex justify-center pt-2">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center z-10 border-2 shadow-lg",
              event.risk === 'low' ? "bg-slate-900 border-slate-700 text-slate-400" : "bg-slate-900 border-yellow-500 text-yellow-500"
            )}>
              {event.icon}
            </div>
          </div>
          <div className="bg-slate-800/40 p-6 rounded-2xl border border-white/5 hover:border-white/10 transition-all group">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div>
                <span className="text-xs font-bold text-blue-500 uppercase tracking-tighter">{event.time}</span>
                <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">{event.action}</h3>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1 text-slate-400">
                  <Users className="w-3 h-3" />
                  {event.actor}
                </div>
                <div className="flex items-center gap-1 text-slate-400">
                  <Globe className="w-3 h-3" />
                  {event.location}
                </div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed italic">
              " {event.explanation} "
            </p>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Assessment:</span>
              <span className="text-[10px] font-bold text-green-500/80 uppercase">Individually Explainable</span>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const Screen4 = () => (
  <div className="max-w-6xl mx-auto py-12 px-6">
    <div className="grid lg:grid-cols-2 gap-12">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider mb-6">
          <Zap className="w-3 h-3" /> AI Engine Synthesis
        </div>
        <h2 className="text-4xl font-bold text-white mb-6">The AI Interpretation</h2>
        <p className="text-lg text-slate-400 mb-8 leading-relaxed">
          While the legacy system sees five safe events, the AI Engine recognizes a single, high-risk journey. It synthesizes weak signals that are invisible to rigid rules.
        </p>

        <div className="bg-slate-800/50 rounded-3xl p-8 border border-white/10 relative overflow-hidden">
          <div className="relative z-10">
            <h4 className="text-slate-500 font-bold text-xs uppercase tracking-widest mb-6">Synthesis Report</h4>
            <div className="flex items-start gap-6 mb-8">
              <div className="w-20 h-20 rounded-2xl bg-red-500/20 flex items-center justify-center border border-red-500/30">
                <AlertTriangle className="text-red-500 w-10 h-10" />
              </div>
              <div>
                <div className="text-red-500 font-bold text-3xl">CRITICAL RISK</div>
                <p className="text-slate-300 mt-1">High-probability Account Takeover Journey detected.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-slate-400">
                <span>Pattern Match Score</span>
                <span className="text-white font-bold">98.4%</span>
              </div>
              <div className="h-2 w-full bg-slate-900 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }} 
                  animate={{ width: '98.4%' }} 
                  transition={{ delay: 0.5, duration: 1.5 }}
                  className="h-full bg-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)]" 
                />
              </div>
              <p className="text-xs text-red-400/80 italic font-medium">
                Recommendation: Immediate Account Freeze and Manual Voice Verification.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="space-y-6">
        {aiInsights.map((insight, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className="p-6 rounded-2xl bg-slate-900 border border-white/5 hover:border-blue-500/30 transition-all"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-bold text-lg">{insight.title}</h3>
              <div className="w-12 h-1 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: `${insight.weight * 100}%` }} />
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">{insight.detail}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

const Screen5 = () => (
  <div className="max-w-4xl mx-auto py-12 px-6">
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="text-center space-y-6 mb-16"
    >
      <h2 className="text-4xl font-bold text-white">How AI Reached This Conclusion</h2>
      <p className="text-slate-400 text-lg">
        Demystifying the "Black Box." The AI isn't using magic; it's using relationship intelligence and temporal context that legacy systems ignore.
      </p>
    </motion.div>

    <div className="grid gap-6">
      {[
        { 
          title: "Signal Fusion", 
          desc: "Rules check A OR B. AI checks (A AND B) as they relate to C in the context of Time D.",
          icon: <Zap className="text-blue-400" />
        },
        { 
          title: "Temporal Velocity", 
          desc: "A vendor update is fine. A vendor update 2 hours after an MFA change and 1 hour before a wire is highly correlated with fraud.",
          icon: <Clock className="text-purple-400" />
        },
        { 
          title: "Peer-Account Learning", 
          desc: "AI identifies that the specific sequence of actions has been observed in 42 similar fraud cases globally in the last 24 hours.",
          icon: <Users className="text-green-400" />
        },
        { 
          title: "Individual Baseline", 
          desc: "Sarah Jenkins has never updated MFA settings and a vendor template within the same week in 5 years of history.",
          icon: <Activity className="text-yellow-400" />
        }
      ].map((item, i) => (
        <motion.div 
          key={i}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.15 }}
          className="flex gap-6 p-8 rounded-3xl bg-slate-800/30 border border-white/5 items-center group hover:bg-slate-800/50 transition-colors"
        >
          <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center shrink-0 border border-white/5 group-hover:border-blue-500/50 transition-colors">
            {item.icon}
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
            <p className="text-slate-400 leading-relaxed">{item.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const Screen6 = () => (
  <div className="max-w-6xl mx-auto py-12 px-6">
    <div className="flex flex-col md:flex-row gap-12">
      <div className="flex-1">
        <h2 className="text-4xl font-bold text-white mb-6">Augmented <br/><span className="text-blue-500">Human Decisioning</span></h2>
        <p className="text-lg text-slate-400 mb-8 leading-relaxed">
          The goal of AI is not to replace the bank's team, but to give them superpowers. Faster triage, automated summaries, and prioritized workflows.
        </p>
        
        <div className="space-y-6">
          <div className="p-6 rounded-2xl bg-blue-500/10 border border-blue-500/20">
            <h4 className="text-blue-400 font-bold text-xs uppercase tracking-widest mb-4">Operational Efficiency Gains</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-900 rounded-xl border border-white/5">
                <div className="text-2xl font-bold text-white">85%</div>
                <div className="text-xs text-slate-500">Reduction in Manual Review Time</div>
              </div>
              <div className="p-4 bg-slate-900 rounded-xl border border-white/5">
                <div className="text-2xl font-bold text-white">4x</div>
                <div className="text-xs text-slate-500">Faster Response to Emerging Threats</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 bg-slate-900 rounded-3xl border border-white/10 overflow-hidden flex flex-col shadow-2xl">
        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-slate-800/30">
          <div className="flex items-center gap-3">
            <Search className="text-slate-500 w-5 h-5" />
            <span className="text-white font-bold">Investigator Workspace</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs text-red-500 font-bold uppercase tracking-widest">High Priority</span>
          </div>
        </div>
        
        <div className="p-8 space-y-6 flex-1">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Case Summary</label>
            <div className="p-4 bg-slate-800/50 rounded-xl text-slate-300 text-sm leading-relaxed border border-white/5">
              Automated Analysis: Account Sarah Jenkins (CFO) shows high correlation with modern "Social Engineering" patterns. MFA modification immediately followed by new high-value wire template. Recommend voice call and identity validation.
            </div>
          </div>

          <div className="space-y-4">
            <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Suggested Actions</label>
            <button className="w-full p-4 rounded-xl bg-red-600 text-white font-bold flex items-center justify-between hover:bg-red-500 transition-colors">
              <span>Freeze Account & Notify User</span>
              <ShieldCheck className="w-5 h-5" />
            </button>
            <button className="w-full p-4 rounded-xl bg-slate-800 text-white font-bold flex items-center justify-between border border-white/10 hover:bg-slate-700 transition-colors">
              <span>Request Voice Verification</span>
              <Smartphone className="w-5 h-5" />
            </button>
            <button className="w-full p-4 rounded-xl bg-slate-800 text-white font-bold flex items-center justify-between border border-white/10 hover:bg-slate-700 transition-colors">
              <span>Mark as False Positive</span>
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 bg-slate-800/30 text-center border-t border-white/10">
          <p className="text-slate-500 text-xs">AI-Generated Context reduces analyst triage from 20 mins to 2 mins.</p>
        </div>
      </div>
    </div>
  </div>
);

const Screen7 = () => (
  <div className="max-w-4xl mx-auto py-20 px-6 text-center">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-12"
    >
      <div className="w-24 h-24 bg-blue-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-blue-500/40 rotate-12">
        <ShieldCheck className="text-white w-12 h-12" />
      </div>
      
      <div className="space-y-4">
        <h2 className="text-5xl font-extrabold text-white">Evolve Your Defense</h2>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Don't just add more rules. Add a layer that understands intent, context, and the full journey.
        </p>
      </div>

      <div className="grid gap-4 max-w-xl mx-auto text-left">
        {[
          "Sophisticated fraudsters stay inside the rulebook.",
          "The fraud journey is made of events that are each individually explainable.",
          "Rules see events. AI sees journeys."
        ].map((text, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + i * 0.2 }}
            className="p-6 rounded-2xl bg-slate-800/50 border border-white/10 flex items-center gap-4 group hover:border-blue-500/30 transition-all"
          >
            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 font-bold shrink-0">
              {i + 1}
            </div>
            <p className="text-white font-semibold text-lg">{text}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="pt-12"
      >
        <button 
          onClick={() => window.location.reload()}
          className="px-8 py-4 rounded-full bg-white text-slate-900 font-bold hover:bg-slate-200 transition-all shadow-xl"
        >
          Restart Demo
        </button>
      </motion.div>
    </motion.div>
  </div>
);

// --- Main App ---

export default function App() {
  const [screen, setScreen] = useState<Screen>(1);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  if (!isLoaded) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans selection:bg-blue-500/30 selection:text-blue-200">
      {/* Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-900/20 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-900/20 blur-[120px] rounded-full" />
      </div>

      <Header />
      
      <main className="relative z-10 pb-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={screen}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {screen === 1 && <Screen1 />}
            {screen === 2 && <Screen2 />}
            {screen === 3 && <Screen3 />}
            {screen === 4 && <Screen4 />}
            {screen === 5 && <Screen5 />}
            {screen === 6 && <Screen6 />}
            {screen === 7 && <Screen7 />}
          </motion.div>
        </AnimatePresence>
      </main>

      <Navigation current={screen} setScreen={setScreen} />
      <ProgressBar current={screen} />
    </div>
  );
}
